import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ProbabilityRace (feedback visualisation)
 *
 * Flagship upgrades (feedback-focused):
 * - One-click classic preset demos (2d6 sums 2–12, coin flip, uneven spinner)
 * - Theoretical vs experimental callout panel (live updating)
 * - Demo pacing defaults (finishLine=50, totalTrials=120) + fast mode that still shows movement
 * - Misconception-first callouts (dice sums combinations + short-run variability)
 * - Replay with same seed vs new seed toggle (variability demo)
 *
 * Config (ProbabilityRaceConfig.java):
 * {
 *   preset?: "dice_sums_2d6" | "coin_flip" | "spinner_uneven",
 *   racers?: [{ label, weight?, theoreticalProb?, colorHex? }],
 *   finishLine?: 50,
 *   totalTrials?: 120,
 *   mode?: "STEP_BY_STEP" | "FAST_FORWARD" | "INSTANT",
 *   seed?: 42,
 *   showPresets?: true,
 *   showTheoryPanel?: true,
 *   misconceptionKey?: "DICE_SUMS_COMBINATIONS" | ...,
 *   feedbackText?: "..."
 * }
 */
export const ProbabilityRace = ({ config }) => {
  const cfg = config || {};

  // --------- small utils ----------
  const safeInt = (n, fb) => (Number.isFinite(n) ? Math.trunc(n) : fb);
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const safeNum = (n, fb) => (Number.isFinite(n) ? n : fb);

  // Deterministic RNG (mulberry32) for "replay same seed"
  const mulberry32 = (seed) => {
    let a = (seed >>> 0) || 1;
    return () => {
      a |= 0;
      a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const toPresetConfig = (presetKey) => {
    if (presetKey === "coin_flip") {
      return {
        preset: "coin_flip",
        finishLine: 40,
        totalTrials: 120,
        mode: "FAST_FORWARD",
        seed: 42,
        racers: [
          { label: "Heads", weight: 1, colorHex: "#f59e0b" },
          { label: "Tails", weight: 1, colorHex: "#3b82f6" },
        ],
        misconceptionKey: "SHORT_RUNS_SURPRISE",
      };
    }

    if (presetKey === "spinner_uneven") {
      return {
        preset: "spinner_uneven",
        finishLine: 40,
        totalTrials: 120,
        mode: "FAST_FORWARD",
        seed: 42,
        racers: [
          { label: "Red", weight: 3, colorHex: "#ef4444" },
          { label: "Blue", weight: 2, colorHex: "#3b82f6" },
          { label: "Green", weight: 1, colorHex: "#22c55e" },
        ],
        misconceptionKey: "WEIGHTS_DRIVE_PROBABILITY",
      };
    }

    // default: dice sums 2d6
    return {
      preset: "dice_sums_2d6",
      finishLine: 50,
      totalTrials: 160,
      mode: "FAST_FORWARD",
      seed: 42,
      racers: [
        { label: "2", weight: 1, colorHex: "#94a3b8" },
        { label: "3", weight: 2, colorHex: "#64748b" },
        { label: "4", weight: 3, colorHex: "#475569" },
        { label: "5", weight: 4, colorHex: "#334155" },
        { label: "6", weight: 5, colorHex: "#1f2937" },
        { label: "7", weight: 6, colorHex: "#6366f1" },
        { label: "8", weight: 5, colorHex: "#1f2937" },
        { label: "9", weight: 4, colorHex: "#334155" },
        { label: "10", weight: 3, colorHex: "#475569" },
        { label: "11", weight: 2, colorHex: "#64748b" },
        { label: "12", weight: 1, colorHex: "#94a3b8" },
      ],
      misconceptionKey: "DICE_SUMS_COMBINATIONS",
    };
  };

  const defaultedCfg = useMemo(() => {
    const preset = (cfg.preset || "").toString().trim() || "dice_sums_2d6";
    const def = toPresetConfig(preset);

    const finishLine = clamp(safeInt(cfg.finishLine, def.finishLine), 10, 200);
    const totalTrials = clamp(safeInt(cfg.totalTrials, def.totalTrials), 20, 2000);
    const mode = (cfg.mode || def.mode || "FAST_FORWARD").toString();
    const seed = Number.isFinite(cfg.seed) ? cfg.seed : def.seed;

    const showPresets = cfg.showPresets !== false;
    const showTheoryPanel = cfg.showTheoryPanel !== false;

    const racers = Array.isArray(cfg.racers) && cfg.racers.length ? cfg.racers : def.racers;

    return {
      preset,
      finishLine,
      totalTrials: Math.max(totalTrials, finishLine),
      mode,
      seed,
      showPresets,
      showTheoryPanel,
      misconceptionKey: (cfg.misconceptionKey || def.misconceptionKey || "").toString(),
      feedbackText: (cfg.feedbackText || "").toString(),
      racers,
    };
  }, [cfg]);

  const [activeCfg, setActiveCfg] = useState(defaultedCfg);

  useEffect(() => {
    // Keep user interactions, but if upstream config changes, refresh baseline.
    setActiveCfg(defaultedCfg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultedCfg.preset, defaultedCfg.finishLine, defaultedCfg.totalTrials, defaultedCfg.mode, defaultedCfg.seed]);

  const normalised = useMemo(() => {
    const inRacers = Array.isArray(activeCfg.racers) ? activeCfg.racers : [];
    const racers = inRacers
      .filter(Boolean)
      .slice(0, 20)
      .map((r, idx) => ({
        label: (r.label ?? `Outcome ${idx + 1}`).toString(),
        weight: Number.isFinite(r.weight) ? Math.max(0, r.weight) : null,
        theoreticalProb: Number.isFinite(r.theoreticalProb) ? Math.max(0, r.theoreticalProb) : null,
        colorHex: (r.colorHex ?? "").toString().trim() || null,
      }));

    // Choose weights if any exist, else probs
    const anyWeight = racers.some((r) => r.weight != null);
    const anyProb = racers.some((r) => r.theoreticalProb != null);

    let out = racers;

    if (anyWeight) {
      const sum = racers.reduce((acc, r) => acc + (r.weight ?? 0), 0);
      if (sum > 0) {
        out = racers.map((r) => ({
          ...r,
          theoreticalProb: (r.weight ?? 0) / sum,
        }));
      } else {
        out = racers.map((r) => ({ ...r, weight: 1, theoreticalProb: 1 / racers.length }));
      }
    } else if (anyProb) {
      const sum = racers.reduce((acc, r) => acc + (r.theoreticalProb ?? 0), 0);
      if (sum > 0) {
        out = racers.map((r) => ({
          ...r,
          weight: (r.theoreticalProb ?? 0) / sum,
          theoreticalProb: (r.theoreticalProb ?? 0) / sum,
        }));
      } else {
        out = racers.map((r) => ({ ...r, weight: 1, theoreticalProb: 1 / racers.length }));
      }
    } else {
      out = racers.map((r) => ({ ...r, weight: 1, theoreticalProb: 1 / racers.length }));
    }

    const defaultColors = ["#6366f1", "#22c55e", "#ef4444", "#f59e0b", "#06b6d4", "#a855f7", "#14b8a6", "#64748b"];

    out = out.map((r, idx) => ({
      ...r,
      colorHex: r.colorHex && /^#?[0-9a-fA-F]{6}$/.test(r.colorHex) ? (r.colorHex.startsWith("#") ? r.colorHex : `#${r.colorHex}`) : defaultColors[idx % defaultColors.length],
    }));

    // Build CDF for weighted sampling
    const cdf = [];
    let cum = 0;
    out.forEach((r) => {
      cum += r.weight ?? 0;
      cdf.push(cum);
    });
    const total = cum || 1;

    return { racers: out, cdf, total };
  }, [activeCfg.racers]);

  // -------- simulation state ----------
  const [running, setRunning] = useState(false);
  const [trial, setTrial] = useState(0);
  const [counts, setCounts] = useState(() => normalised.racers.map(() => 0));
  const [winnerIndex, setWinnerIndex] = useState(null);

  // seed control: same seed vs new seed
  const [seedMode, setSeedMode] = useState("same"); // "same" | "new"
  const seedRef = useRef(activeCfg.seed ?? 42);
  const rngRef = useRef(mulberry32(seedRef.current));

  // Reset when config/preset changes
  useEffect(() => {
    resetAll("keepSeed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCfg.preset, activeCfg.finishLine, activeCfg.totalTrials, activeCfg.mode, normalised.racers.length]);

  const resetAll = (seedStrategy = "keepSeed") => {
    setRunning(false);
    setWinnerIndex(null);
    setTrial(0);
    setCounts(normalised.racers.map(() => 0));

    if (seedStrategy === "newSeed") {
      const newSeed = Date.now() & 0xffffffff;
      seedRef.current = newSeed;
      rngRef.current = mulberry32(newSeed);
    } else {
      // keep / reset to activeCfg seed
      const s = safeInt(activeCfg.seed, 42) & 0xffffffff;
      seedRef.current = s;
      rngRef.current = mulberry32(s);
    }
  };

  const pickIndex = () => {
    const r = rngRef.current() * normalised.total;
    // linear scan (small list)
    for (let i = 0; i < normalised.cdf.length; i++) {
      if (r <= normalised.cdf[i]) return i;
    }
    return normalised.cdf.length - 1;
  };

  const applyTrials = (k) => {
    setCounts((prev) => {
      const next = prev.slice();
      let w = winnerIndex;

      for (let t = 0; t < k; t++) {
        const i = pickIndex();
        next[i] = (next[i] ?? 0) + 1;

        if (w == null && next[i] >= activeCfg.finishLine) {
          w = i;
        }
      }

      // winnerIndex is separate state; update after loop
      if (w != null) setWinnerIndex((cur) => (cur == null ? w : cur));
      return next;
    });

    setTrial((prev) => {
      const n = prev + k;
      return Math.min(n, activeCfg.totalTrials);
    });
  };

  // Interval loop (STEP_BY_STEP / FAST_FORWARD)
  useEffect(() => {
    if (!running) return undefined;
    if (winnerIndex != null) return undefined;
    if (trial >= activeCfg.totalTrials) return undefined;

    const mode = (activeCfg.mode || "FAST_FORWARD").toString().toUpperCase();
    if (mode === "INSTANT") return undefined;

    const tickMs = mode === "STEP_BY_STEP" ? 320 : 60;
    const batch = mode === "STEP_BY_STEP" ? 1 : 5;

    const id = window.setInterval(() => {
      // stop conditions checked inside too
      if (winnerIndex != null) return;
      if (trial >= activeCfg.totalTrials) return;

      const remaining = activeCfg.totalTrials - trial;
      const k = Math.max(1, Math.min(batch, remaining));
      applyTrials(k);
    }, tickMs);

    return () => window.clearInterval(id);
  }, [running, activeCfg.mode, activeCfg.totalTrials, trial, winnerIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-stop at end / winner
  useEffect(() => {
    if (!running) return;
    if (winnerIndex != null || trial >= activeCfg.totalTrials) {
      setRunning(false);
    }
  }, [running, winnerIndex, trial, activeCfg.totalTrials]);

  const startOrPause = () => {
    const mode = (activeCfg.mode || "FAST_FORWARD").toString().toUpperCase();

    // INSTANT mode: compute all remaining immediately but still show movement via CSS transitions on bars.
    if (!running && mode === "INSTANT") {
      const remaining = activeCfg.totalTrials - trial;
      if (remaining <= 0) return;
      applyTrials(remaining);
      return;
    }

    setRunning((p) => !p);
  };

  const stepOnce = () => {
    setRunning(false);
    if (winnerIndex != null) return;
    if (trial >= activeCfg.totalTrials) return;
    applyTrials(1);
  };

  const onPresetClick = (presetKey) => {
    const next = toPresetConfig(presetKey);
    setActiveCfg((prev) => ({
      ...prev,
      ...next,
      showPresets: true,
      showTheoryPanel: true,
    }));
    setSeedMode("same");
    // reset (keeps preset seed)
    setTimeout(() => resetAll("keepSeed"), 0);
  };

  const toggleSeedMode = () => {
    setSeedMode((m) => {
      const next = m === "same" ? "new" : "same";
      // apply immediately
      resetAll(next === "new" ? "newSeed" : "keepSeed");
      return next;
    });
  };

  const exp = useMemo(() => {
    const t = Math.max(1, trial);
    return counts.map((c) => c / t);
  }, [counts, trial]);

  const maxAbsErr = useMemo(() => {
    if (!activeCfg.showTheoryPanel) return null;
    if (!normalised.racers.length) return null;
    let m = 0;
    for (let i = 0; i < normalised.racers.length; i++) {
      const e = Math.abs((exp[i] ?? 0) - (normalised.racers[i].theoreticalProb ?? 0));
      if (e > m) m = e;
    }
    return m;
  }, [activeCfg.showTheoryPanel, exp, normalised.racers]);

  const converging = maxAbsErr != null && trial >= 30 && maxAbsErr <= 0.05;

  const titlePreset = (activeCfg.preset || "").toString().replaceAll("_", " ");
  const hasPresets = activeCfg.showPresets !== false;

  const callout = useMemo(() => {
    const p = (activeCfg.preset || "").toString();
    const key = (activeCfg.misconceptionKey || "").toString();

    if (p === "dice_sums_2d6" || key === "DICE_SUMS_COMBINATIONS") {
      const leader = counts.reduce((best, c, i) => (best == null || c > counts[best] ? i : best), null);
      const leaderLabel = leader != null ? normalised.racers[leader]?.label : null;
      const line1 = "Dice sums: 7 is most likely because it has the most combinations (6 out of 36).";
      const line2 = trial < 20
        ? "Early on, the ‘winner’ can bounce around — short runs can look surprising."
        : leaderLabel && leaderLabel !== "7"
          ? `Right now ${leaderLabel} is ahead — but over more trials, 7 usually pulls ahead.`
          : "Over many trials, frequencies tend to stabilise near the theoretical curve.";
      return { tone: "info", lines: [line1, line2] };
    }

    if (key === "SHORT_RUNS_SURPRISE") {
      return { tone: "info", lines: ["Short runs can be surprising.", "More trials usually stabilise the pattern."] };
    }

    if (key === "WEIGHTS_DRIVE_PROBABILITY") {
      return { tone: "info", lines: ["Heavier sectors win more often.", "The theoretical probability comes from the sector weights."] };
    }

    return null;
  }, [activeCfg.preset, activeCfg.misconceptionKey, counts, trial, normalised.racers]);

  // ---------- render ----------
  return (
    <div className="ch-probrace-widget">
      <div className="ch-probrace-header">
        <div>
          <h3 className="ch-probrace-title">Probability Race</h3>
          <div className="ch-probrace-subtitle">
            <span className="ch-probrace-pill">{titlePreset || "race"}</span>
            <span className="ch-probrace-meta">
              Finish {activeCfg.finishLine} · Trials {trial}/{activeCfg.totalTrials} · Seed {seedMode === "same" ? seedRef.current : "random"}
            </span>
          </div>
        </div>

        <div className="ch-probrace-actions">
          <button type="button" className="ch-btn-outline" onClick={() => resetAll(seedMode === "new" ? "newSeed" : "keepSeed")} title="Reset">
            Reset
          </button>
        </div>
      </div>

      {activeCfg.feedbackText && (
        <div className="ch-probrace-feedback">
          {activeCfg.feedbackText}
        </div>
      )}

      {callout && (
        <div className={`ch-probrace-callout ${callout.tone}`}>
          <strong>Why?</strong>
          <div>{callout.lines[0]}</div>
          <div>{callout.lines[1]}</div>
        </div>
      )}

      {hasPresets && (
        <div className="ch-probrace-presets">
          <button type="button" className="ch-btn-outline" onClick={() => onPresetClick("dice_sums_2d6")}>Two dice sums (2–12)</button>
          <button type="button" className="ch-btn-outline" onClick={() => onPresetClick("coin_flip")}>Coin flips (H vs T)</button>
          <button type="button" className="ch-btn-outline" onClick={() => onPresetClick("spinner_uneven")}>Uneven spinner</button>

          <button type="button" className="ch-btn-outline" onClick={toggleSeedMode} title="Replay with same seed vs new random seed">
            {seedMode === "same" ? "Replay same seed" : "Use fixed seed"}
          </button>
        </div>
      )}

      <div className="ch-probrace-controls">
        <button type="button" className="ch-btn" onClick={startOrPause} disabled={winnerIndex != null || trial >= activeCfg.totalTrials}>
          {running ? "Pause" : (activeCfg.mode || "").toString().toUpperCase() === "INSTANT" ? "Run" : "Start"}
        </button>

        <button type="button" className="ch-btn-outline" onClick={stepOnce} disabled={winnerIndex != null || trial >= activeCfg.totalTrials}>
          Step
        </button>

        <span className="ch-probrace-status">
          {winnerIndex != null
            ? `Winner: ${normalised.racers[winnerIndex]?.label}`
            : trial >= activeCfg.totalTrials
              ? "Finished (trials limit)"
              : running
                ? "Running…"
                : "Ready"}
        </span>
      </div>

      <div className="ch-probrace-racers">
        {normalised.racers.map((r, i) => {
          const c = counts[i] ?? 0;
          const progress = Math.min(1, c / activeCfg.finishLine);
          const theo = r.theoreticalProb ?? 0;
          const expP = exp[i] ?? 0;
          const delta = expP - theo;

          return (
            <div key={`r-${i}`} className={`ch-probrace-racer ${winnerIndex === i ? "ch-is-winner" : ""}`}>
              <div className="ch-probrace-racer-head">
                <span className="ch-probrace-swatch" style={{ background: r.colorHex }} />
                <span className="ch-probrace-label">{r.label}</span>
                <span className="ch-probrace-count">{c}</span>
              </div>

              <div className="ch-probrace-track">
                <div
                  className="ch-probrace-progress"
                  style={{
                    width: `${progress * 100}%`,
                    background: r.colorHex,
                    transition: "width 120ms linear",
                  }}
                />
                <div className="ch-probrace-finish" />
              </div>

              {activeCfg.showTheoryPanel !== false && (
                <div className="ch-probrace-stats">
                  <span>theory: {(theo * 100).toFixed(1)}%</span>
                  <span>exp: {(expP * 100).toFixed(1)}%</span>
                  <span className={`ch-probrace-delta ${delta >= 0 ? "ch-is-pos" : "ch-is-neg"}`}>
                    Δ {(delta * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {activeCfg.showTheoryPanel !== false && (
        <div className="ch-feedback-panel ch-probrace-theorypanel">
        <div className="ch-feedback-panel-header">
          <div className="ch-feedback-panel-title-row">
            <div className="ch-feedback-panel-title">Theoretical vs experimental</div>
            {converging ? (
              <span className="ch-feedback-pill ch-is-good">Converging ✓</span>
            ) : (
              <span className="ch-feedback-pill ch-is-mid">Still settling…</span>
            )}
          </div>
        </div>
        <div className="ch-feedback-panel-body">
          <div className="ch-probrace-theoryhint">The experimental frequency updates as trials accumulate. Short runs can look noisy; longer runs usually stabilise.</div>
        </div>
      </div>
      )}
    </div>
  );
};
